import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { Controller } from 'react-hook-form';
import TagsInput from '../../../collums-components/form/TagsInput';
import HelpIcon from '@material-ui/icons/Info';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography
} from '@material-ui/core';
import SelectionList from '../../common/SelectionList';
import { serviceDetailsModalStyles } from '../styles';
import { Autocomplete } from '@material-ui/lab';
import { SERVICE_AVAILABILITIES, MEDICAL_TYPES } from '../../../collums-constants';
import ColorPicker from '../../common/ColorPicker';
import { getLocationItem } from '../../../collums-constants/utils';
import CategoriesModal from '../../categories/CategoriesModal';
import OrganisationsApi from '../../../api/OrganisationsApi';

function GeneralTab({
    categories = [],
    subCategories = [],
    searchCategories,
    searchSubCategories,
    services = [],
    control,
    register,
    watch,
    currentService,
    setValue,
    isFromOrg,
    loadServices,
    selectedCategory,
    selectedSubCategory,
    setSelectedCategory,
    setSelectedSubCategory,
    setAllowCommission,
    setCommissionCondition,
    clinic,
    allClinics,
    onCategoryConfirm
}) {
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const classes = makeStyles(serviceDetailsModalStyles)();
    const isAddOn = watch('isAddOn');
    const [organisationData, setOrganisationData] = useState(null);

    useEffect(() => {
        (async () => {
            const organisation = await OrganisationsApi.getOrganisation();
            setOrganisationData(organisation);
        })();
    }, []);

    useEffect(() => {
        register('category');
        register('subCategory');
        /* eslint-disable-next-line */
    }, []);

    const tooltipAvailability = () => (
        <Tooltip
            placement="right"
            title={
                <Typography style={{ fontSize: '14px', display: 'grid' }}>
                    <span style={{ paddingBottom: '8px' }}>
                        &quot;Book & sell&quot; allows services to be booked in the calendar and sold via the POS. Most
                        services will have this setting.
                    </span>
                    <span style={{ paddingBottom: '8px' }}>
                        &quot;Book only&quot; does not allow services to be sold or added to invoices - an example of
                        this could be local anaesthetic that needs to be booked but you don&apos;t charge for.
                    </span>
                    <span>
                        &quot;Sell only&quot; is the best setting for a late cancellation fee or tips - these cannot be
                        booked in the calendar but can be added to an invoice.
                    </span>
                </Typography>
            }
            aria-label="help"
        >
            <HelpIcon className={classes.helpIcon} />
        </Tooltip>
    );
    return (
        <div className={classes.formContent}>
            <div className={classes.formContentRow}>
                <FormControl className={classes.formItem} variant="outlined">
                    <InputLabel shrink>Name *</InputLabel>
                    <TextField
                        defaultValue={currentService ? currentService.name : ''}
                        inputRef={register({ required: 'Name' })}
                        name="name"
                        variant="outlined"
                        inputProps={{ readOnly: !isFromOrg && allClinics.length > 1 }}
                    />
                </FormControl>
                <FormControl className={classes.formItem} variant="outlined">
                    <InputLabel shrink>Description</InputLabel>
                    <TextField
                        defaultValue={currentService ? currentService.description : ''}
                        inputRef={register()}
                        name="description"
                        variant="outlined"
                        inputProps={{ readOnly: !isFromOrg && allClinics.length > 1 }}
                    />
                </FormControl>
                <FormControl className={classes.formItem} style={{ display: 'inline-block' }} variant="outlined">
                    <InputLabel>Availability *</InputLabel>
                    <Controller
                        style={{ width: 'calc(100% - 20px)' }}
                        disabled={!isFromOrg && allClinics.length > 1}
                        name="availability"
                        rules={{ required: 'Availabity' }}
                        control={control}
                        defaultValue={currentService ? currentService.availability : ''}
                        as={
                            <Select
                                inputProps={{
                                    className: classes.selectInputProps
                                }}
                                defaultValue={SERVICE_AVAILABILITIES.BOOK_AND_SELL}
                            >
                                <MenuItem value={undefined}>
                                    <em>None</em>
                                </MenuItem>

                                <MenuItem value={SERVICE_AVAILABILITIES.SELL_ONLY}>Sell Only</MenuItem>
                                <MenuItem value={SERVICE_AVAILABILITIES.BOOK_AND_SELL}>Book & Sell</MenuItem>
                                <MenuItem value={SERVICE_AVAILABILITIES.BOOK_ONLY}>Book Only</MenuItem>
                            </Select>
                        }
                    ></Controller>
                    {tooltipAvailability()}
                </FormControl>
                <FormControl className={classes.formItem} variant="outlined">
                    <InputLabel shrink>Category *</InputLabel>
                    <Autocomplete
                        // disabled={!isFromOrg}
                        disabled={!isFromOrg && allClinics.length > 1}
                        disableClearable
                        options={categories}
                        value={selectedCategory}
                        getOptionLabel={option => option.name}
                        className={classes.autocomplete}
                        defaultValue={currentService.category ? categories[0] : null}
                        onChange={async (e, value) => {
                            if (!value || !value.id) {
                                setShowCategoryModal(true);
                                return false;
                            }
                            setAllowCommission(
                                isFromOrg ? value.allowCommission : getLocationItem(value, clinic)?.allowCommission
                            );
                            setCommissionCondition(
                                isFromOrg
                                    ? value.commissionCondition
                                    : getLocationItem(value, clinic)?.commissionCondition
                            );
                            setValue('category', value !== null ? value.id : undefined);
                            searchSubCategories('', value !== null ? value.id : undefined);
                            setSelectedCategory(value);
                            setValue('subCategory', undefined);
                            setSelectedSubCategory(null);
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                onKeyDown={event => event.preventDefault()}
                                onChange={e => searchCategories(e.target.value)}
                                variant="outlined"
                                style={{ width: '100%' }}
                            />
                        )}
                        renderOption={option => {
                            return <p className={classes.autocompleteOptions}>{`${option.name}`}</p>;
                        }}
                    />
                </FormControl>
                <FormControl className={classes.formItem} variant="outlined">
                    <InputLabel shrink>Sub Category</InputLabel>
                    <Autocomplete
                        disabled={!isFromOrg && allClinics.length > 1}
                        value={selectedSubCategory}
                        options={subCategories}
                        className={classes.autocomplete}
                        getOptionLabel={option => option.name}
                        defaultValue={currentService.subCategory ? subCategories[0] : null}
                        onChange={async (e, value) => {
                            setValue('subCategory', value !== null ? value.id : undefined);
                            setSelectedSubCategory(value);
                        }}
                        renderInput={params => (
                            <TextField
                                {...params}
                                onChange={e =>
                                    searchSubCategories(
                                        e.target.value,
                                        selectedCategory !== null ? (selectedCategory || {}).id : undefined
                                    )
                                }
                                variant="outlined"
                                style={{ width: '100%' }}
                            />
                        )}
                        renderOption={option => {
                            return <p className={classes.autocompleteOptions}>{`${option.name}`}</p>;
                        }}
                    />
                </FormControl>
                {organisationData?.isVat0Only && (
                    <FormControl className={classes.formItem} variant="outlined" style={{ display: 'inline-block' }}>
                        <InputLabel>Label service as</InputLabel>
                        <Controller
                            style={{ width: 'calc(100% - 20px)' }}
                            name="medical"
                            control={control}
                            defaultValue={currentService.medical || ''}
                            as={
                                <Select>
                                    <MenuItem key="empty-medical" value="">
                                        &nbsp;
                                    </MenuItem>
                                    {Object.entries(MEDICAL_TYPES).map(([key, label]) => (
                                        <MenuItem key={key} value={key}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            }
                        ></Controller>
                        <Tooltip
                            placement="right"
                            title={
                                <Typography style={{ fontSize: '14px', display: 'grid' }}>
                                    If you wish, label your services as cosmetic or medical to help with your reporting.
                                    <br />
                                    This label can be changed on the invoice at checkout by clicking on the circle icon.
                                    <br />
                                    If you have selected &quot;All my sales have 0% tax&quot; in Settings, changing this
                                    label will have no effect on the tax on the invoice. If you have NOT made this
                                    selection, changing the label on the invoice will change the tax (0% for medical,
                                    20% for cosmetic).
                                </Typography>
                            }
                            aria-label="help"
                        >
                            <HelpIcon className={classes.helpIcon} />
                        </Tooltip>
                    </FormControl>
                )}

                <div className={classes.isAddOnContainer}>
                    <FormControlLabel
                        label="Is an add-on"
                        disabled={!isFromOrg && allClinics.length > 1}
                        control={
                            <Checkbox
                                defaultChecked={currentService ? currentService.isAddOn : false}
                                inputProps={{ name: 'isAddOn' }}
                                inputRef={register}
                            />
                        }
                    />
                    <Tooltip
                        title={
                            <Typography style={{ fontSize: 14 }}>
                                Add-ons cannot be booked on their own – they must be booked after another service.
                            </Typography>
                        }
                        aria-label="help"
                    >
                        <HelpIcon className={classes.helpIcon} />
                    </Tooltip>

                    <FormControlLabel
                        label="Is a remote service"
                        disabled={!isFromOrg && allClinics.length > 1}
                        control={
                            <Checkbox
                                defaultChecked={currentService ? currentService.isRemote : false}
                                inputProps={{ name: 'isRemote' }}
                                inputRef={register}
                            />
                        }
                        style={{ marginLeft: 20 }}
                    />
                    <Tooltip
                        title={
                            <Typography style={{ fontSize: 14 }}>
                                If selected, when this service is booked, the client will receive the customised
                                &quot;Remote service&quot; confirmation notification. Set this in
                                Admin&gt;Notifications.
                            </Typography>
                        }
                        aria-label="help"
                    >
                        <HelpIcon className={classes.helpIcon} />
                    </Tooltip>

                    <FormControlLabel
                        label="Is a consultation"
                        disabled={!isFromOrg && allClinics.length > 1}
                        control={
                            <Checkbox
                                defaultChecked={currentService ? currentService.isConsultation : false}
                                inputProps={{ name: 'isConsultation' }}
                                inputRef={register}
                            />
                        }
                        style={{ marginLeft: 20 }}
                    />
                    <Tooltip
                        title={
                            <Typography style={{ fontSize: 14 }}>
                                For conversion reporting, mark this service as a consultation - any service booked after
                                this will be counted as a conversion.
                            </Typography>
                        }
                        aria-label="help"
                    >
                        <HelpIcon className={classes.helpIcon} />
                    </Tooltip>
                </div>

                <div />

                {isAddOn && (
                    <SelectionList
                        disabled={!isFromOrg && allClinics.length > 1}
                        label="Services for this add on"
                        control={control}
                        rules={register}
                        options={_.sortBy(services, 'name').map(service => ({
                            value: service.id,
                            label: service.name,
                            subtitle: service.onlineDescription
                        }))}
                        defaultValue={currentService ? currentService.addOnServices : []}
                        name="addOnServices"
                        isPaginated={true}
                        action={loadServices}
                    />
                )}
                <ColorPicker
                    disabled={!isFromOrg && allClinics.length > 1}
                    inputRef={register}
                    name="colour"
                    defaultValue={currentService?.colour}
                    props={{
                        style: {
                            marginTop: 20,
                            marginLeft: 0
                        }
                    }}
                />
                <TagsInput
                    disabled={!isFromOrg && allClinics.length > 1}
                    name="tags"
                    control={control}
                    className={classes.formItem}
                    canAdd
                    defaultValue={currentService ? currentService.tags : []}
                />
                <FormControlLabel
                    label="Sell as part of course only"
                    disabled={!isFromOrg && allClinics.length > 1}
                    control={
                        <Checkbox
                            defaultChecked={currentService ? currentService.courseOnly : false}
                            inputProps={{ name: 'courseOnly' }}
                            inputRef={register}
                        />
                    }
                />
            </div>
            {showCategoryModal && (
                <CategoriesModal
                    isFromOrg={isFromOrg}
                    key={'new'}
                    categoryId={'new'}
                    category={null}
                    onConfirm={response => {
                        categories.push(response.response);
                        setSelectedCategory(response.response);
                        setShowCategoryModal(false);
                        onCategoryConfirm();
                    }}
                    onCancel={() => {
                        setShowCategoryModal(false);
                    }}
                    methodType={'Create'}
                    clinic={clinic}
                    disableBackdropClick={true}
                    allClinics={allClinics}
                />
            )}
        </div>
    );
}

GeneralTab.propTypes = {
    register: PropTypes.func.isRequired,
    categories: PropTypes.array,
    services: PropTypes.array,
    control: PropTypes.any,
    watch: PropTypes.any
};

export default GeneralTab;
